import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  UploadCloud, FileText, Gauge, BookOpen, PlayCircle, ShieldAlert,
  CheckCircle2, AlertTriangle, XCircle, HelpCircle, Bell,
} from "lucide-react";
import { usePublicLang, LangToggle } from "../lib/publicLang";

const T = {
  fr: {
    title: "Comment utiliser ANATRADEX",
    subtitle: "Le guide de démarrage et d'interprétation des résultats — 5 minutes de lecture.",
    replay: "Revoir l'assistant de démarrage",
    stepsTitle: "Démarrage en 3 étapes",
    s1t: "1. Importez vos trades réels",
    s1a: "Exportez votre historique depuis MT4/MT5 (Terminal → Historique → clic droit → Rapport), cTrader (History → Export) ou tout CSV, puis glissez-déposez le fichier dans ",
    s1link: "Importer mes trades",
    s1b: ". Aucune connexion à votre broker n'est requise.",
    s2t: "2. Générez votre Rapport d'Analyse Statistique",
    s2p: "Après l'import, cliquez « Générer mon rapport PDF ». Le moteur exécute les tests de robustesse (PBO, Deflated Sharpe, permutations) sur VOS trades. Chaque chiffre est reproductible : le seed est affiché sur le rapport.",
    s3t: "3. Lisez le verdict — puis décidez",
    s3a: "Le rapport répond à une seule question : ",
    s3b: "votre edge est-il statistiquement réel, ou est-ce du bruit ?",
    s3c: " Les seuils d'interprétation sont détaillés ci-dessous.",
    metricsTitle: "Interpréter les indicateurs",
    metricsSub: "Les 4 tests clés du rapport, expliqués sans jargon.",
    pboT: "PBO — Probability of Backtest Overfitting",
    pboP: "La probabilité que votre stratégie soit « sur-ajustée » au passé : elle a l'air performante sur l'historique mais ne tiendra pas en réel. Calculée par CSCV (combinatorially symmetric cross-validation, Bailey & López de Prado).",
    pbo1: "< 20 %", pbo1d: " — robuste : la performance ne dépend pas d'un découpage chanceux",
    pbo2: "20-50 %", pbo2d: " — zone grise : prudence, testez sur plus de données",
    pbo3: "> 50 %", pbo3d: " — overfitting probable : l'edge apparent est vraisemblablement du bruit",
    dsrT: "Deflated Sharpe Ratio (DSR)",
    dsrP: "Un Sharpe classique peut être gonflé par la chance, un historique court, ou le fait d'avoir essayé plusieurs stratégies avant de garder la meilleure. Le DSR « dégonfle » le Sharpe en corrigeant ces biais (skewness, kurtosis, nombre d'essais).",
    dsr1: "DSR > 0,95", dsr1d: " — le Sharpe observé est significatif à 95 % : difficilement attribuable à la chance",
    dsr2: "DSR < 0,95", dsr2d: " — le Sharpe observé peut s'expliquer par la chance seule",
    permT: "Test de permutation (sign-flip)",
    permP: "On mélange aléatoirement le signe de vos trades des milliers de fois : si des trades « au hasard » font aussi bien que les vôtres, votre edge n'en est pas un. La p-value mesure cette probabilité.",
    perm1: "p < 0,05", perm1d: " — moins de 5 % de chances que le hasard produise ce résultat : edge significatif",
    perm2: "p ≥ 0,05", perm2d: " — impossible d'exclure le hasard : ne pas augmenter le risque sur cette base",
    pkfT: "Purged K-Fold CV (validation croisée purgée)",
    pkfP1: "Utilisée dans le ML Lab : au lieu de valider un modèle sur des données qui « touchent » celles de l'entraînement (fuite temporelle = résultats trompeurs), on ",
    pkfPurge: "purge", pkfP2: " les observations voisines et on ajoute un ", pkfEmbargo: "embargo",
    pkfP3: " entre les blocs. Résultat : une estimation honnête de la performance hors-échantillon. Si la performance s'effondre en Purged K-Fold par rapport au backtest simple, c'est un signal d'overfitting.",
    whereTitle: "Où trouver quoi",
    w1l: "Importer mes trades", w1: " — CSV MT4 / MT5 / cTrader / générique, puis rapport PDF sur vos données réelles.",
    w2l: "Tableau de bord", w2: " — graphique avec indicateurs classiques + SMC (Order Blocks, FVG, kill-zones). Le filtre « zones non mitigées » garde l'écran lisible en forte volatilité.",
    w3l: "Alertes", w3: " — notifications Telegram, dont l'alerte killzone (prix entrant dans une zone non mitigée pendant les sessions London / New York).",
    w4l: "Méthodologie complète", w4: " — toutes les formules, publiques et reproductibles, avec le script de vérification.",
    w5l: "Lexique", w5: " — tous les termes techniques (PBO, DSR, ZII, CSD...) au format « En clair / Technique / Interprétation / Utilisation », avec tableau récapitulatif.",
    faqTitle: "Questions fréquentes",
    q1: "Mon PBO est élevé, ma stratégie est-elle morte ?",
    a1: "Pas nécessairement — cela signifie que l'historique fourni ne permet pas de distinguer votre edge du hasard. Souvent : trop peu de trades (< 100), ou une période trop favorable. Ajoutez de l'historique et re-testez avant de conclure.",
    q2: "Mes données de trading sont-elles privées ?",
    a2: "Oui. Vos trades importés sont isolés par compte (multi-tenant), jamais partagés ni revendus. Aucune connexion à votre broker : vous n'importez qu'un fichier d'historique.",
    q3: "Pourquoi les résultats sont-ils « reproductibles » ?",
    a3: "Chaque rapport affiche son seed aléatoire. N'importe qui peut relancer le calcul avec ce seed et obtenir exactement les mêmes chiffres — c'est notre garantie anti « chiffres magiques ».",
    q4: "Un doute, un bug, une suggestion ?",
    a4: "Utilisez le bouton « Feedback beta » dans la barre latérale — votre message arrive directement chez le fondateur.",
    disclaimer: "ANATRADEX est un outil d'analyse statistique, pas un conseil en investissement. Un verdict statistique favorable ne garantit aucune performance future. Le trading comporte un risque de perte pouvant atteindre 100 % du capital.",
  },
  en: {
    title: "How to use ANATRADEX",
    subtitle: "The getting-started and results-interpretation guide — a 5-minute read.",
    replay: "Replay the setup assistant",
    stepsTitle: "Getting started in 3 steps",
    s1t: "1. Import your real trades",
    s1a: "Export your history from MT4/MT5 (Terminal → Account History → right click → Report), cTrader (History → Export) or any CSV, then drag & drop the file into ",
    s1link: "Import my trades",
    s1b: ". No broker connection required.",
    s2t: "2. Generate your Statistical Analysis Report",
    s2p: "After importing, click \"Generate my PDF report\". The engine runs the robustness tests (PBO, Deflated Sharpe, permutations) on YOUR trades. Every number is reproducible: the seed is displayed on the report.",
    s3t: "3. Read the verdict — then decide",
    s3a: "The report answers a single question: ",
    s3b: "is your edge statistically real, or is it noise?",
    s3c: " The interpretation thresholds are detailed below.",
    metricsTitle: "Interpreting the indicators",
    metricsSub: "The report's 4 key tests, explained without jargon.",
    pboT: "PBO — Probability of Backtest Overfitting",
    pboP: "The probability that your strategy is \"overfitted\" to the past: it looks great on history but won't hold up live. Computed via CSCV (combinatorially symmetric cross-validation, Bailey & López de Prado).",
    pbo1: "< 20%", pbo1d: " — robust: performance doesn't depend on a lucky data split",
    pbo2: "20-50%", pbo2d: " — grey zone: be careful, test on more data",
    pbo3: "> 50%", pbo3d: " — likely overfitting: the apparent edge is probably noise",
    dsrT: "Deflated Sharpe Ratio (DSR)",
    dsrP: "A classic Sharpe can be inflated by luck, a short track record, or trying several strategies and keeping the best one. The DSR \"deflates\" the Sharpe by correcting for these biases (skewness, kurtosis, number of trials).",
    dsr1: "DSR > 0.95", dsr1d: " — the observed Sharpe is significant at 95%: hard to attribute to luck",
    dsr2: "DSR < 0.95", dsr2d: " — the observed Sharpe can be explained by luck alone",
    permT: "Permutation test (sign-flip)",
    permP: "We randomly shuffle the sign of your trades thousands of times: if \"random\" trades do as well as yours, your edge isn't one. The p-value measures that probability.",
    perm1: "p < 0.05", perm1d: " — less than a 5% chance that randomness produces this result: significant edge",
    perm2: "p ≥ 0.05", perm2d: " — randomness can't be ruled out: don't increase risk on this basis",
    pkfT: "Purged K-Fold CV (purged cross-validation)",
    pkfP1: "Used in the ML Lab: instead of validating a model on data that \"touches\" the training data (temporal leakage = misleading results), we ",
    pkfPurge: "purge", pkfP2: " neighboring observations and add an ", pkfEmbargo: "embargo",
    pkfP3: " between blocks. Result: an honest estimate of out-of-sample performance. If performance collapses under Purged K-Fold versus the plain backtest, that's an overfitting signal.",
    whereTitle: "Where to find what",
    w1l: "Import my trades", w1: " — MT4 / MT5 / cTrader / generic CSV, then a PDF report on your real data.",
    w2l: "Dashboard", w2: " — chart with classic indicators + SMC (Order Blocks, FVG, kill-zones). The \"unmitigated zones\" filter keeps the screen readable in high volatility.",
    w3l: "Alerts", w3: " — Telegram notifications, including the killzone alert (price entering an unmitigated zone during London / New York sessions).",
    w4l: "Full methodology", w4: " — every formula, public and reproducible, with the verification script.",
    w5l: "Glossary", w5: " — every technical term (PBO, DSR, ZII, CSD...) in a \"Plain / Technical / Interpretation / Usage\" format, with a summary table.",
    faqTitle: "Frequently asked questions",
    q1: "My PBO is high — is my strategy dead?",
    a1: "Not necessarily — it means the provided history cannot distinguish your edge from luck. Often: too few trades (< 100), or an overly favorable period. Add more history and re-test before concluding.",
    q2: "Is my trading data private?",
    a2: "Yes. Your imported trades are isolated per account (multi-tenant), never shared or resold. No broker connection: you only upload a history file.",
    q3: "Why are the results \"reproducible\"?",
    a3: "Every report displays its random seed. Anyone can re-run the computation with that seed and get exactly the same numbers — our guarantee against \"magic numbers\".",
    q4: "A doubt, a bug, a suggestion?",
    a4: "Use the \"Feedback beta\" button in the sidebar — your message goes straight to the founder.",
    disclaimer: "ANATRADEX is a statistical analysis tool, not investment advice. A favorable statistical verdict guarantees no future performance. Trading carries a risk of loss of up to 100% of capital.",
  },
};

export default function UserGuide() {
  const navigate = useNavigate();
  const [lang, setLang] = usePublicLang();
  const L = T[lang] || T.fr;

  useEffect(() => {
    try { localStorage.setItem("anatradex-guide-visited", "1"); } catch { /* ignore */ }
  }, []);

  return (
    <div className="space-y-8 max-w-4xl" data-testid="user-guide-page">
      <div>
        <div className="flex items-center justify-between gap-3">
          <h1 className="font-heading font-bold text-2xl mb-1.5">{L.title}</h1>
          <LangToggle lang={lang} setLang={setLang} />
        </div>
        <p className="text-white/60 text-sm">{L.subtitle}</p>
        <button
          onClick={() => navigate("/app?onboarding=1")}
          className="mt-3 inline-flex items-center gap-2 text-sm text-accent-blue hover:underline"
          data-testid="guide-replay-tour-btn"
        >
          <PlayCircle size={15} /> {L.replay}
        </button>
      </div>

      <section className="panel p-6" data-testid="guide-steps-section">
        <h2 className="font-heading font-bold text-lg mb-4">{L.stepsTitle}</h2>
        <ol className="space-y-4">
          <li className="flex gap-3">
            <div className="w-8 h-8 rounded-lg bg-accent-blue/15 border border-accent-blue/30 flex items-center justify-center shrink-0"><UploadCloud size={15} className="text-accent-blue" /></div>
            <div>
              <div className="font-semibold text-sm mb-0.5">{L.s1t}</div>
              <p className="text-white/60 text-sm">{L.s1a}<Link to="/app/import" className="text-accent-blue underline">{L.s1link}</Link>{L.s1b}</p>
            </div>
          </li>
          <li className="flex gap-3">
            <div className="w-8 h-8 rounded-lg bg-accent-blue/15 border border-accent-blue/30 flex items-center justify-center shrink-0"><FileText size={15} className="text-accent-blue" /></div>
            <div>
              <div className="font-semibold text-sm mb-0.5">{L.s2t}</div>
              <p className="text-white/60 text-sm">{L.s2p}</p>
            </div>
          </li>
          <li className="flex gap-3">
            <div className="w-8 h-8 rounded-lg bg-accent-blue/15 border border-accent-blue/30 flex items-center justify-center shrink-0"><Gauge size={15} className="text-accent-blue" /></div>
            <div>
              <div className="font-semibold text-sm mb-0.5">{L.s3t}</div>
              <p className="text-white/60 text-sm">{L.s3a}<b className="text-white">{L.s3b}</b>{L.s3c}</p>
            </div>
          </li>
        </ol>
      </section>

      <section data-testid="guide-metrics-section">
        <h2 className="font-heading font-bold text-lg mb-1.5">{L.metricsTitle}</h2>
        <p className="text-white/50 text-sm mb-4">{L.metricsSub}</p>
        <div className="space-y-4">
          <div className="panel p-5" data-testid="guide-metric-pbo">
            <div className="font-semibold mb-1">{L.pboT}</div>
            <p className="text-white/60 text-sm mb-3">{L.pboP}</p>
            <div className="grid sm:grid-cols-3 gap-2 text-xs">
              <div className="rounded-lg border border-up/30 bg-up/10 p-2.5"><CheckCircle2 size={13} className="text-up inline mr-1" /><b className="text-up">{L.pbo1}</b>{L.pbo1d}</div>
              <div className="rounded-lg border border-warn/30 bg-warn/10 p-2.5"><AlertTriangle size={13} className="text-warn inline mr-1" /><b className="text-warn">{L.pbo2}</b>{L.pbo2d}</div>
              <div className="rounded-lg border border-down/30 bg-down/10 p-2.5"><XCircle size={13} className="text-down inline mr-1" /><b className="text-down">{L.pbo3}</b>{L.pbo3d}</div>
            </div>
          </div>
          <div className="panel p-5" data-testid="guide-metric-dsr">
            <div className="font-semibold mb-1">{L.dsrT}</div>
            <p className="text-white/60 text-sm mb-3">{L.dsrP}</p>
            <div className="grid sm:grid-cols-2 gap-2 text-xs">
              <div className="rounded-lg border border-up/30 bg-up/10 p-2.5"><CheckCircle2 size={13} className="text-up inline mr-1" /><b className="text-up">{L.dsr1}</b>{L.dsr1d}</div>
              <div className="rounded-lg border border-down/30 bg-down/10 p-2.5"><XCircle size={13} className="text-down inline mr-1" /><b className="text-down">{L.dsr2}</b>{L.dsr2d}</div>
            </div>
          </div>
          <div className="panel p-5" data-testid="guide-metric-permutation">
            <div className="font-semibold mb-1">{L.permT}</div>
            <p className="text-white/60 text-sm mb-3">{L.permP}</p>
            <div className="grid sm:grid-cols-2 gap-2 text-xs">
              <div className="rounded-lg border border-up/30 bg-up/10 p-2.5"><CheckCircle2 size={13} className="text-up inline mr-1" /><b className="text-up">{L.perm1}</b>{L.perm1d}</div>
              <div className="rounded-lg border border-down/30 bg-down/10 p-2.5"><XCircle size={13} className="text-down inline mr-1" /><b className="text-down">{L.perm2}</b>{L.perm2d}</div>
            </div>
          </div>
          <div className="panel p-5" data-testid="guide-metric-purged-kfold">
            <div className="font-semibold mb-1">{L.pkfT}</div>
            <p className="text-white/60 text-sm">{L.pkfP1}<b className="text-white">{L.pkfPurge}</b>{L.pkfP2}<b className="text-white">{L.pkfEmbargo}</b>{L.pkfP3}</p>
          </div>
        </div>
      </section>

      <section className="panel p-6" data-testid="guide-features-section">
        <h2 className="font-heading font-bold text-lg mb-4">{L.whereTitle}</h2>
        <ul className="space-y-2.5 text-sm">
          <li className="flex items-start gap-2.5"><UploadCloud size={15} className="text-accent-blue mt-0.5 shrink-0" /><span><Link to="/app/import" className="text-accent-blue underline">{L.w1l}</Link>{L.w1}</span></li>
          <li className="flex items-start gap-2.5"><Gauge size={15} className="text-accent-blue mt-0.5 shrink-0" /><span><Link to="/app" className="text-accent-blue underline">{L.w2l}</Link>{L.w2}</span></li>
          <li className="flex items-start gap-2.5"><Bell size={15} className="text-accent-blue mt-0.5 shrink-0" /><span><Link to="/app/alerts" className="text-accent-blue underline">{L.w3l}</Link>{L.w3}</span></li>
          <li className="flex items-start gap-2.5"><BookOpen size={15} className="text-accent-blue mt-0.5 shrink-0" /><span><Link to="/methodology" className="text-accent-blue underline">{L.w4l}</Link>{L.w4}</span></li>
          <li className="flex items-start gap-2.5"><BookOpen size={15} className="text-accent-blue mt-0.5 shrink-0" /><span><Link to="/app/lexique" className="text-accent-blue underline" data-testid="guide-lexique-link">{L.w5l}</Link>{L.w5}</span></li>
        </ul>
      </section>

      <section className="panel p-6" data-testid="guide-faq-section">
        <h2 className="font-heading font-bold text-lg mb-4 flex items-center gap-2"><HelpCircle size={17} /> {L.faqTitle}</h2>
        <div className="space-y-4 text-sm">
          {[["q1", "a1"], ["q2", "a2"], ["q3", "a3"], ["q4", "a4"]].map(([q, a]) => (
            <div key={q}>
              <div className="font-semibold mb-1">{L[q]}</div>
              <p className="text-white/60">{L[a]}</p>
            </div>
          ))}
        </div>
      </section>

      <p className="text-[11px] text-white/35 leading-relaxed flex items-start gap-1.5" data-testid="guide-risk-disclaimer">
        <ShieldAlert size={13} className="shrink-0 mt-0.5" />
        <span>{L.disclaimer}</span>
      </p>
    </div>
  );
}
